import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

const loadUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// ── Thunks ────────────────────────────────────────────
export const loginEmail = createAsyncThunk('auth/loginEmail', async (creds, { rejectWithValue }) => {
  try {
    const { data } = await authAPI.loginEmail(creds);
    return data.data;
  } catch (e) {
    const msg = typeof e === 'string'
      ? e
      : e?.response?.data?.message || e?.response?.data?.msg || e?.message || e?.msg || 'Login failed';
    return rejectWithValue(msg);
  }
});

export const loginOtp = createAsyncThunk('auth/loginOtp', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await authAPI.verifyOtp(payload);
    return data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.message || 'OTP verification failed'); }
});

export const loginSocial = createAsyncThunk('auth/loginSocial', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await authAPI.socialLogin(payload);
    return data.data;
  } catch (e) { return rejectWithValue(e.response?.data?.message || e.response?.data?.msg || 'Social login failed'); }
});

export const logoutThunk = createAsyncThunk('auth/logout', async (_, { getState }) => {
  const refreshToken = localStorage.getItem('refreshToken');
  try { await authAPI.logout({ refreshToken }); } catch (_) {}
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
});

// ── Slice ─────────────────────────────────────────────
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user        : loadUser(),
    accessToken : localStorage.getItem('accessToken'),
    loading     : false,
    error       : null,
  },
  reducers: {
    setCredentials: (state, { payload }) => {
      state.user        = payload.user;
      state.accessToken = payload.accessToken;
      localStorage.setItem('accessToken',  payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken);
      localStorage.setItem('user',         JSON.stringify(payload.user));
    },
    clearAuth: (state) => {
      state.user        = null;
      state.accessToken = null;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    },
    updateUser: (state, { payload }) => {
      state.user = { ...state.user, ...payload };
      localStorage.setItem('user', JSON.stringify(state.user));
    },
  },
  extraReducers: (b) => {
    // loginEmail
    b.addCase(loginEmail.pending,   (s) => { s.loading = true; s.error = null; });
    b.addCase(loginEmail.fulfilled, (s, { payload }) => {
      s.loading     = false;
      s.user        = payload.user;
      s.accessToken = payload.accessToken;
      localStorage.setItem('accessToken',  payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken);
      localStorage.setItem('user',         JSON.stringify(payload.user));
      toast.success('Welcome back!');
    });
    b.addCase(loginEmail.rejected,  (s, { payload }) => { s.loading = false; s.error = payload; toast.error(payload); });

    // loginOtp
    b.addCase(loginOtp.pending,     (s) => { s.loading = true; s.error = null; });
    b.addCase(loginOtp.fulfilled,   (s, { payload }) => {
      s.loading     = false;
      s.user        = payload.user;
      s.accessToken = payload.accessToken;
      localStorage.setItem('accessToken',  payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken);
      localStorage.setItem('user',         JSON.stringify(payload.user));
      toast.success(payload.isNew ? 'Account created!' : 'Welcome back!');
    });
    b.addCase(loginOtp.rejected,    (s, { payload }) => { s.loading = false; s.error = payload; toast.error(payload); });

    // loginSocial
    b.addCase(loginSocial.pending,   (s) => { s.loading = true; s.error = null; });
    b.addCase(loginSocial.fulfilled, (s, { payload }) => {
      s.loading     = false;
      const user = payload.user || payload;
      s.user        = user;
      s.accessToken = payload.accessToken;
      localStorage.setItem('accessToken',  payload.accessToken);
      localStorage.setItem('refreshToken', payload.refreshToken);
      localStorage.setItem('user',         JSON.stringify(user));
      toast.success('Welcome back!');
    });
    b.addCase(loginSocial.rejected,  (s, { payload }) => { s.loading = false; s.error = payload; toast.error(payload); });

    // logout
    b.addCase(logoutThunk.fulfilled, (s) => { s.user = null; s.accessToken = null; });
  },
});

export const { setCredentials, clearAuth, updateUser } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectUser      = (s) => s.auth.user;
export const selectIsAuth    = (s) => !!s.auth.user;
export const selectAuthLoading=(s) => s.auth.loading;
export const selectRole      = (s) => s.auth.user?.role;
export const selectIsAdmin   = (s) => ['super_admin','admin','moderator'].includes(s.auth.user?.role);
export const selectIsBusiness= (s) => ['business_owner','business_staff'].includes(s.auth.user?.role);
